import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { supersessionChain } from '../../utils/partSupersession';

type Row = { partNumber?: unknown; partId?: unknown; part_number?: unknown; extra_details?: Record<string, unknown> | null };

const normalize = (v: unknown) => String(v ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

/**
 * "SS" badge next to an OE number when the part sits in a replacement chain
 * (amber when THIS number is obsolete, navy when it is the current one).
 * Click opens the full chain, oldest first, with this number highlighted. The
 * panel is portaled to <body> so table cells / clipped rows cannot cut it off.
 */
const SupersessionIndicator: React.FC<{ row: Row }> = ({ row }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - 336) });
    };
    place();
    const close = (e: MouseEvent) => {
      const el = e.target as Node;
      if (!btnRef.current?.contains(el) && !panelRef.current?.contains(el)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const levels = supersessionChain(row);
  if (levels.length < 2) return null;
  // The backend moves an obsolete row onto its CURRENT number and keeps the
  // catalog id as-is, so "this part" is the number, never the id — and the
  // id must not make a current row read as replaced.
  const own = new Set([row.partNumber, row.part_number].map(normalize).filter(Boolean));
  if (!own.size && row.partId) own.add(normalize(row.partId));
  const ownIndex = levels.findIndex(level => level.some(n => own.has(normalize(n))));
  const replaced = ownIndex >= 0 && ownIndex < levels.length - 1
    ? levels.slice(ownIndex + 1).map(level => level.join(' + ')).join(' → ')
    : null;
  // One badge colour everywhere (Ben, 2026-09-12); obsolete vs current is
  // said inside the popup, not by the badge.
  const color = '#1E0DB8';
  const bg = '#eef0ff';
  const chainLabel = t('chat.clarification.detail.supersessionchain', { defaultValue: 'Supersession chain' });
  const label = replaced
    ? `${t('imageWithCallouts.partInfo.replacedBy', { defaultValue: 'Replaced by' })} ${replaced}`
    : chainLabel;
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={{ display: 'inline-flex', alignSelf: 'flex-start', flex: 'none', width: 'fit-content', alignItems: 'center', verticalAlign: 'middle', marginLeft: 4, padding: '0 5px', height: 16, fontSize: 9, fontWeight: 800, letterSpacing: '0.04em', color, background: bg, border: `1px solid ${color}55`, borderRadius: 6, cursor: 'pointer', lineHeight: 1, fontFamily: 'inherit' }}
      >
        SS
      </button>
      {open && pos && createPortal(
        <span
          ref={panelRef}
          role="dialog"
          onClick={(e) => e.stopPropagation()}
          // Max z-index: the fullscreen diagram modal sits at 2147483647 and the
          // panel is appended to <body> after it, so equal z-index paints it on top.
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 2147483647, minWidth: 240, maxWidth: 340, padding: '8px 10px', background: '#fff', border: '1px solid #e0def5', borderRadius: 8, boxShadow: '0 6px 20px rgba(30,13,184,0.12)', fontFamily: 'ui-monospace, monospace', fontSize: 11, fontWeight: 600, lineHeight: '18px', textAlign: 'left', whiteSpace: 'normal', color: '#201e1a' }}
        >
          <span style={{ display: 'block', fontFamily: 'inherit', fontSize: 9, fontWeight: 700, color: '#9b97c4', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 2 }}>
            {chainLabel}
          </span>
          {/* One sentence first: what this number became. */}
          {replaced && (
            <span style={{ display: 'block', fontFamily: 'Inter Tight, system-ui, sans-serif', fontSize: 11, fontWeight: 500, color: '#444', marginBottom: 8, lineHeight: '15px' }}>
              {t('imageWithCallouts.partInfo.supersessionSummary', { defaultValue: '{{from}} is obsolete. Order {{to}} instead.', from: levels.find(l => l.some(n => own.has(normalize(n))))?.join(' + ') ?? '', to: levels[levels.length - 1].join(' + ') })}
            </span>
          )}
          {levels.map((level, i) => {
            const here = level.some(n => own.has(normalize(n)));
            const last = i === levels.length - 1;
            const tone = last ? '#1E0DB8' : '#6b6b6b';
            return (
              <React.Fragment key={i}>
                {i > 0 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Inter Tight, system-ui, sans-serif', fontSize: 9, fontWeight: 600, color: '#9b97c4', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: '14px', paddingLeft: 2 }}>
                    <span aria-hidden style={{ fontSize: 11 }}>↓</span>
                    {t('imageWithCallouts.partInfo.replacedBy', { defaultValue: 'Replaced by' })}
                  </span>
                )}
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px', borderRadius: 4, background: here ? bg : 'transparent', border: here ? `1px solid ${color}55` : '1px solid transparent' }}>
                  <span style={{ color: here ? color : tone, fontWeight: last || here ? 800 : 600 }}>{level.join(' + ')}</span>
                  <span style={{ marginLeft: 'auto', fontFamily: 'Inter Tight, system-ui, sans-serif', fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap', color: last ? '#15803d' : '#b45309' }}>
                    {here ? `${t('imageWithCallouts.partInfo.thisPart', { defaultValue: 'this part' })} · ` : ''}
                    {last ? t('imageWithCallouts.partInfo.currentPart', { defaultValue: 'current' }) : t('imageWithCallouts.partInfo.obsoletePart', { defaultValue: 'obsolete' })}
                  </span>
                </span>
              </React.Fragment>
            );
          })}
        </span>,
        document.body,
      )}
    </>
  );
};

export default SupersessionIndicator;
